import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Users, Briefcase, Rocket } from "lucide-react";
import { scrollToId } from "@/pages/LandingPage";

const OPTIONS = [
  { id: "family", icon: Users, label: "My family" },
  { id: "business", icon: Briefcase, label: "My business" },
  { id: "partner", icon: Rocket, label: "A business of my own" },
];

export default function StickyPathBar() {
  const [show, setShow] = useState(false);
  const [chooser, setChooser] = useState(false);

  useEffect(() => {
    const alreadyEngaged = () => sessionStorage.getItem("mll_path_started");
    const onScroll = () => {
      if (alreadyEngaged()) {
        setShow(false);
        return;
      }
      const nearBottom = window.scrollY > document.body.scrollHeight - window.innerHeight - 1400;
      if (window.scrollY > document.body.scrollHeight * 0.45) setChooser(true);
      setShow(window.scrollY > 500 && !nearBottom);
    };
    const timer = setTimeout(() => {
      if (alreadyEngaged() || sessionStorage.getItem("mll_form_started")) return;
      const nearBottom = window.scrollY > document.body.scrollHeight - window.innerHeight - 1400;
      if (!nearBottom) {
        setChooser(true);
        setShow(true);
      }
    }, 5000);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, []);

  const pick = (id) => {
    sessionStorage.setItem("mll_path_started", "1");
    window.__track?.("path_start");
    window.dispatchEvent(new CustomEvent("path-select", { detail: id }));
    setShow(false);
    scrollToId("#your-path");
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 inset-x-0 z-50 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[340px] px-3 pb-4 pt-3 sm:p-5 bg-[#0B192C]/95 backdrop-blur-md border-t sm:border border-white/10 sm:rounded-3xl sm:shadow-2xl sm:shadow-blue-950/40"
          data-testid="sticky-path-bar"
        >
          {chooser ? (
            <div data-testid="sticky-chooser">
              <p className="font-display font-bold text-white text-sm sm:text-base mb-3 leading-snug">
                Quick question — what brings you here?
              </p>
              <div className="flex flex-col gap-2">
                {OPTIONS.map((o) => (
                  <button
                    key={o.id}
                    data-testid={`sticky-option-${o.id}`}
                    onClick={() => pick(o.id)}
                    className="flex items-center gap-3 w-full text-left bg-white/[0.06] hover:bg-sky-400 text-slate-200 hover:text-[#0B192C] font-medium text-sm px-4 py-2.5 rounded-xl transition-colors duration-200"
                  >
                    <o.icon size={16} className="shrink-0" />
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <button
              data-testid="sticky-path-button"
              onClick={() => scrollToId("#your-path")}
              className="w-full flex items-center justify-center gap-2 bg-sky-400 active:bg-sky-300 text-[#0B192C] font-semibold py-3.5 rounded-full transition-colors duration-200"
            >
              Find my path — 30 seconds
              <ArrowRight size={16} />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
